import { useState, useEffect } from 'react';
import { Bell, BellOff, BellRing, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PushNotificationToggle = ({ userId, userType = 'business' }) => {
  const { t } = useTranslation();
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSupported, setIsSupported] = useState(true);
  const [permissionState, setPermissionState] = useState('default');

  useEffect(() => {
    checkPushSupport();
  }, []);

  const checkPushSupport = async () => {
    // Check if Push API and Service Workers are supported
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setIsSupported(false);
      setIsLoading(false);
      return;
    }

    // Check notification permission
    const permission = Notification.permission;
    setPermissionState(permission);

    // Check if already subscribed
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      setIsSubscribed(!!subscription);

      // Also check server status
      const headerKey = userType === 'business' ? 'X-Business-Id' : 'X-User-Id';
      const res = await fetch(`${API}/push/status`, {
        headers: { [headerKey]: userId }
      });
      if (res.ok) {
        const data = await res.json();
        setIsSubscribed(data.is_subscribed);
      }
    } catch (err) {
      console.error('Error checking push status:', err);
    }

    setIsLoading(false);
  };

  const subscribeToPush = async () => {
    setIsLoading(true);

    try {
      // Request notification permission
      const permission = await Notification.requestPermission();
      setPermissionState(permission);

      if (permission !== 'granted') {
        toast.error(t('push_notifications.permission_denied'));
        setIsLoading(false);
        return;
      }

      // Get VAPID public key from server
      const vapidRes = await fetch(`${API}/push/vapid-key`);
      if (!vapidRes.ok) {
        throw new Error('Push notifications not configured on server');
      }
      const { publicKey } = await vapidRes.json();

      // Register service worker and subscribe
      const registration = await navigator.serviceWorker.ready;
      
      // Convert VAPID key to Uint8Array
      const applicationServerKey = urlBase64ToUint8Array(publicKey);

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey
      });

      // Send subscription to server
      const headerKey = userType === 'business' ? 'X-Business-Id' : 'X-User-Id';
      const res = await fetch(`${API}/push/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          [headerKey]: userId
        },
        body: JSON.stringify(subscription.toJSON())
      });

      if (res.ok) {
        setIsSubscribed(true);
        toast.success(t('push_notifications.enabled'));
      } else {
        throw new Error('Failed to save subscription');
      }
    } catch (err) {
      console.error('Error subscribing to push:', err);
      toast.error(t('push_notifications.error'));
    }

    setIsLoading(false);
  };

  const unsubscribeFromPush = async () => {
    setIsLoading(true);

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        await subscription.unsubscribe();
      }

      // Notify server
      const headerKey = userType === 'business' ? 'X-Business-Id' : 'X-User-Id';
      await fetch(`${API}/push/unsubscribe`, {
        method: 'POST',
        headers: { [headerKey]: userId }
      });

      setIsSubscribed(false);
      toast.success(t('push_notifications.disabled'));
    } catch (err) {
      console.error('Error unsubscribing:', err);
      toast.error(t('push_notifications.error'));
    }

    setIsLoading(false);
  };

  const sendTestNotification = async () => {
    const headerKey = userType === 'business' ? 'X-Business-Id' : 'X-User-Id';
    try {
      const res = await fetch(`${API}/push/test`, {
        method: 'POST',
        headers: { 
          [headerKey]: userId,
          'X-User-Id': userId 
        }
      });
      const data = await res.json();
      if (data.success) {
        toast.success(t('push_notifications.test_sent'));
      } else {
        toast.error(t('push_notifications.test_failed'));
      }
    } catch (err) {
      toast.error(t('push_notifications.test_failed'));
    }
  };

  // Not supported
  if (!isSupported) {
    return (
      <div className="flex items-center gap-3 p-4 bg-gray-100 dark:bg-gray-800 rounded-xl" data-testid="push-not-supported">
        <AlertCircle className="w-5 h-5 text-gray-500" />
        <div>
          <p className="text-sm font-medium">{t('push_notifications.not_supported')}</p>
          <p className="text-xs text-muted-foreground">{t('push_notifications.not_supported_desc')}</p>
        </div>
      </div>
    );
  }

  // Permission denied
  if (permissionState === 'denied') {
    return (
      <div className="flex items-center gap-3 p-4 bg-amber-50 dark:bg-amber-950/20 rounded-xl" data-testid="push-permission-denied">
        <BellOff className="w-5 h-5 text-amber-600" />
        <div>
          <p className="text-sm font-medium text-amber-800 dark:text-amber-200">{t('push_notifications.blocked')}</p>
          <p className="text-xs text-amber-700 dark:text-amber-300">{t('push_notifications.blocked_desc')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl p-4 border border-border/50" data-testid="push-notification-toggle">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
            isSubscribed 
              ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400' 
              : 'bg-gray-100 dark:bg-gray-800 text-gray-500'
          }`}>
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : isSubscribed ? (
              <BellRing className="w-5 h-5" />
            ) : (
              <Bell className="w-5 h-5" />
            )}
          </div>
          <div>
            <p className="font-medium text-sm">{t('push_notifications.title')}</p>
            <p className="text-xs text-muted-foreground">
              {isSubscribed 
                ? t('push_notifications.active') 
                : t('push_notifications.inactive')}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {isSubscribed && (
            <Button
              variant="ghost"
              size="sm"
              onClick={sendTestNotification}
              disabled={isLoading}
              className="text-xs"
              data-testid="push-test-btn"
            >
              {t('push_notifications.test')}
            </Button>
          )}
          
          <Button
            variant={isSubscribed ? 'outline' : 'default'}
            size="sm"
            onClick={isSubscribed ? unsubscribeFromPush : subscribeToPush}
            disabled={isLoading}
            className={isSubscribed ? '' : 'bg-local-sage hover:bg-local-sage/90'}
            data-testid="push-toggle-btn"
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : isSubscribed ? (
              t('push_notifications.disable')
            ) : (
              t('push_notifications.enable')
            )}
          </Button>
        </div>
      </div>

      {isSubscribed && (
        <div className="mt-3 pt-3 border-t border-border/50 flex items-center gap-2 text-xs text-green-600 dark:text-green-400">
          <CheckCircle2 className="w-4 h-4" />
          <span>{t('push_notifications.will_receive')}</span>
        </div>
      )}
    </div>
  );
};

// Helper function to convert VAPID key
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}
